/**
 * LICITARS 3.0 - Protected Route with Role-Based Access Control
 * Implements role detection and redirection based on user permissions
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

interface ProtectedRouteV3Props {
  children: React.ReactNode;
  requiredRole?: "master" | "admin" | "gestor" | "operador";
  allowedRoles?: Array<"master" | "admin" | "gestor" | "operador">;
  redirectTo?: string;
}

/**
 * Protected Route Component with Role-Based Access Control
 * 
 * @param children - Components to render if authorized
 * @param requiredRole - Specific role required (exact match)
 * @param allowedRoles - Array of roles that have access
 * @param redirectTo - Custom redirect path (defaults to /login or /unauthorized)
 * 
 * @example
 * // Master only
 * <ProtectedRouteV3 requiredRole="master">
 *   <MasterDashboard />
 * </ProtectedRouteV3>
 * 
 * @example
 * // Admin or Master
 * <ProtectedRouteV3 allowedRoles={["master", "admin"]}>
 *   <ClientAdmin />
 * </ProtectedRouteV3>
 */
const ProtectedRouteV3: React.FC<ProtectedRouteV3Props> = ({
  children,
  requiredRole,
  allowedRoles,
  redirectTo,
}) => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen w-full bg-navy-800 flex items-center justify-center">
        <div className="glass-card p-8 rounded-lg flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-steel-400 animate-spin" />
          <p className="text-platinum-400 text-sm">Verificando autenticação...</p>
        </div>
      </div>
    );
  }
  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    // Save the current location to return after login
    localStorage.setItem("returnUrl", location.pathname);
    return <Navigate to={redirectTo || "/login"} state={{ from: location }} replace />;
  }
  // Check role-based access
  const userRole = user.role as "master" | "admin" | "gestor" | "operador" | undefined;
  // If specific role is required, check exact match
  if (requiredRole && userRole !== requiredRole) {
    console.warn(`Access denied: User role "${userRole}" does not match required role "${requiredRole}"`);
    return <Navigate to="/unauthorized" replace />;
  }
  // If allowed roles are specified, check if user role is in the list
  if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    console.warn(`Access denied: User role "${userRole}" not in allowed roles: ${allowedRoles.join(", ")}`);
    return <Navigate to="/unauthorized" replace />;
  }
  // User is authenticated and authorized
  return <>{children}</>;
};

export default ProtectedRouteV3;
